import { type ButtonHTMLAttributes } from "react";

type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";
type ButtonSize = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary: "bg-primary text-on-primary hover:bg-primary-hover",
  secondary:
    "border border-gray-300 text-muted hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700",
  danger: "bg-danger text-white hover:bg-red-700",
  ghost: "text-muted hover:bg-gray-100 dark:hover:bg-gray-700",
};

// Below sm every size is at least a 44 px touch target. The floor is max-sm:
// only, so from sm each size is its desktop height and a caller's own min-h-11
// still holds there (an sm: restore would out-rank it).
const sizeClasses: Record<ButtonSize, string> = {
  sm: "px-3 py-1.5 text-xs max-sm:min-h-11",
  md: "px-4 py-2 text-sm max-sm:min-h-11",
  lg: "px-6 py-2.5 text-base max-sm:min-h-11",
};

function Spinner() {
  return (
    <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  disabled,
  children,
  className = "",
  ...props
}: ButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors disabled:opacity-50 ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      {...props}
    >
      {loading && <Spinner />}
      {children}
    </button>
  );
}
